import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react'
import dagre from 'dagre'
import { Box, Typography, Tooltip, IconButton, Button, useTheme } from '@material-ui/core'
import { Check as CheckIcon, CheckCircle as CheckCircleIcon } from '@material-ui/icons'
import ClearIcon from '@material-ui/icons/Clear'
import { alpha } from '@material-ui/core/styles'

// "Cockpit" view of the clustered HPO graph: dagre lays out each cluster top-down,
// clusters tile into rows, and the whole canvas is scaled to fit the frame so the
// full picture is visible at a glance without scrolling. Nodes are kept compact.
// We avoid React Flow on purpose: its ESM build uses syntax (??, ?.) that
// react-scripts 4 / webpack 4 won't transpile from node_modules.
const NODE_W = 188
const NODE_H = 58
const HEADER_H = 34
const CLUSTER_GAP_X = 48
const CLUSTER_GAP_Y = 60
const MAX_ROW_WIDTH = 1500 // prefer wide single-row tiling; wrap only for many clusters
const PAD = 12

function layoutCluster(clusterNodes, clusterEdges) {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'TB', nodesep: 18, ranksep: 34, marginx: 2, marginy: 2 })

  clusterNodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }))
  clusterEdges.forEach((e) => g.setEdge(e.source, e.target))
  dagre.layout(g)

  const positions = {}
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  clusterNodes.forEach((n) => {
    const { x, y } = g.node(n.id) // dagre returns the node center
    const left = x - NODE_W / 2
    const top = y - NODE_H / 2
    positions[n.id] = { x: left, y: top }
    minX = Math.min(minX, left)
    minY = Math.min(minY, top)
    maxX = Math.max(maxX, left + NODE_W)
    maxY = Math.max(maxY, top + NODE_H)
  })

  return { positions, minX, minY, width: maxX - minX, height: maxY - minY }
}

function buildLayout(graph, availWidth) {
  // Render every node from the response. Curation state (added / rejected) is shown
  // purely through styling (nodes are never removed) so accepting a parent-of-many
  // can't orphan its children and shatter the tree into disconnected clusters.
  const nodes = graph.nodes
  const keptIds = new Set(nodes.map((n) => n.id))
  const edges = graph.edges.filter((e) => keptIds.has(e.source) && keptIds.has(e.target))

  const nodeById = {}
  nodes.forEach((n) => {
    nodeById[n.id] = n
  })

  const nodesByCluster = {}
  nodes.forEach((n) => {
    const cluster = keptIds.has(n.cluster) ? n.cluster : n.id // root dropped -> stand alone
    ;(nodesByCluster[cluster] = nodesByCluster[cluster] || []).push(n)
  })

  const edgesByCluster = {}
  edges.forEach((e) => {
    const cluster = keptIds.has(nodeById[e.source]?.cluster) ? nodeById[e.source].cluster : e.source
    ;(edgesByCluster[cluster] = edgesByCluster[cluster] || []).push(e)
  })

  // Order clusters by size desc (clusters[] is pre-sorted; append any stragglers).
  const ordered = (graph.clusters || []).map((c) => c.id).filter((id) => nodesByCluster[id])
  Object.keys(nodesByCluster).forEach((id) => {
    if (!ordered.includes(id)) ordered.push(id)
  })

  // Lay each cluster out once, then decide how many columns to tile them into.
  const metas = ordered.map((clusterId) => {
    const clusterNodes = nodesByCluster[clusterId]
    const clusterEdges = edgesByCluster[clusterId] || []
    const cl = layoutCluster(clusterNodes, clusterEdges)
    const rootName =
      (graph.clusters || []).find((c) => c.id === clusterId)?.root_name || nodeById[clusterId]?.name || ''
    return { clusterId, clusterNodes, rootName, count: clusterNodes.length, ...cl, fullH: cl.height + HEADER_H }
  })

  // Shelf (first-fit row) bin-packing keyed to the visible panel width: fill a row
  // left-to-right and wrap to the next row only when the next cluster would overflow
  // the frame. Wrapping at the *actual* panel width (not a fixed constant) packs
  // clusters tightly. For example, once a wide cluster fills row 1, the next cluster wraps and
  // narrow clusters tuck in beside it instead of being stranded on their own row.
  const wrapW = Math.max(NODE_W + 2 * PAD, Math.min(availWidth || 1200, MAX_ROW_WIDTH))

  const placedNodes = []
  const headers = []
  const positionById = {}
  let canvasW = 0
  let canvasH = 0
  let rowX = PAD
  let rowY = PAD
  let rowMaxH = 0

  metas.forEach((m) => {
    if (rowX > PAD && rowX + m.width > wrapW) {
      rowY += rowMaxH + CLUSTER_GAP_Y
      rowX = PAD
      rowMaxH = 0
    }
    const offsetX = rowX
    const offsetY = rowY
    headers.push({ x: offsetX, y: offsetY, label: m.rootName, count: m.count, width: m.width })

    m.clusterNodes.forEach((n) => {
      const p = m.positions[n.id]
      const x = p.x - m.minX + offsetX
      const y = p.y - m.minY + offsetY + HEADER_H
      positionById[n.id] = { x, y }
      placedNodes.push({ ...n, x, y })
      canvasW = Math.max(canvasW, x + NODE_W)
      canvasH = Math.max(canvasH, y + NODE_H)
    })

    rowX += m.width + CLUSTER_GAP_X
    rowMaxH = Math.max(rowMaxH, m.fullH)
  })

  const placedEdges = edges
    .filter((e) => positionById[e.source] && positionById[e.target])
    .map((e) => {
      const s = positionById[e.source]
      const t = positionById[e.target]
      return {
        id: `${e.source}->${e.target}`,
        x1: s.x + NODE_W / 2,
        y1: s.y + NODE_H,
        x2: t.x + NODE_W / 2,
        y2: t.y,
      }
    })

  return { placedNodes, headers, placedEdges, width: canvasW + PAD, height: canvasH + PAD }
}

// Track the rendered size of an element so we can scale the canvas to fit it.
function useElementSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect
      setSize({ w: cr.width, h: cr.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return size
}

const ChipNode = ({ node, onAccept, onReject, onRemove, onRestore, isSelected, isRejected }) => {
  const theme = useTheme()
  // 'picked' (legacy 'patient') = AI-selected; 'candidate' = retrieved but not picked
  // (add-able, shown muted); 'ancestor' = grouping node only.
  const isPicked = node.kind === 'picked' || node.kind === 'patient'
  const isCandidate = node.kind === 'candidate'
  const accent = isPicked
    ? theme.palette.primary.main
    : isCandidate
    ? theme.palette.warning.main
    : theme.palette.text.secondary
  // Curation state drives which buttons show. Actions only restyle the node; they never
  // remove it from the graph, so the tree never fragments.
  const added = !!isSelected
  const rejected = !!isRejected && !added
  // Neutral nodes: any node can be added (incl. grey grouping terms); only real findings
  // (picked/candidate) can be rejected.
  const showAccept = !added && !rejected && !!onAccept
  const showReject = !added && !rejected && (isPicked || isCandidate) && !!onReject
  const showRemove = added && !!onRemove
  const showRestore = rejected && !!onRestore

  const tooltip = (
    <Box sx={{ maxWidth: 280 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {node.name}
      </Typography>
      <Typography variant="caption" sx={{ color: theme.palette.primary.light, display: 'block', mb: 0.5 }}>
        {node.id}
      </Typography>
      {node.definition && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
          {node.definition}
        </Typography>
      )}
      {node.synonyms && node.synonyms.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
          Synonyms: {node.synonyms.slice(0, 5).join(', ')}
          {node.synonyms.length > 5 ? `, +${node.synonyms.length - 5} more` : ''}
        </Typography>
      )}
      {/* What the node is, stated neutrally so it never contradicts the curation state
          shown below (e.g. an AI-selected term you later dismissed). */}
      {isPicked && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: theme.palette.primary.main }}
        >
          The AI selected this term from your clinical history.
        </Typography>
      )}
      {isCandidate && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: theme.palette.warning.main }}
        >
          A related term the AI suggested but did not select.
        </Typography>
      )}
      {!isPicked && !isCandidate && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          A broader grouping term, shown only to connect related findings. Not one of the patient&apos;s findings.
        </Typography>
      )}
      {/* Current curation state and the action available. Exactly one of these shows. */}
      {added ? (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: theme.palette.success.main }}
        >
          ✓ Added to your analysis. Click ✗ to remove.
        </Typography>
      ) : rejected ? (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: theme.palette.error.main }}
        >
          Dismissed. Click ✓ to restore.
        </Typography>
      ) : (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: theme.palette.text.secondary }}
        >
          {isPicked || isCandidate
            ? 'Click ✓ to add it to your analysis, or ✗ to dismiss.'
            : 'Click ✓ to add it to your analysis.'}
        </Typography>
      )}
    </Box>
  )

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        sx={{
          position: 'absolute',
          left: node.x,
          top: node.y,
          width: NODE_W,
          height: NODE_H,
          px: 0.75,
          borderRadius: 1.5,
          opacity: rejected ? 0.5 : 1,
          border: added
            ? `2px solid ${accent}`
            : isPicked
            ? `1px solid ${accent}`
            : isCandidate
            ? `1px dashed ${alpha(accent, 0.6)}`
            : `1px solid ${alpha(accent, 0.4)}`,
          backgroundColor: added
            ? `${alpha(accent, 0.2)}`
            : isPicked
            ? `${alpha(accent, 0.08)}`
            : isCandidate
            ? `${alpha(accent, 0.04)}`
            : theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.25 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.72rem',
              lineHeight: 1.15,
              fontStyle: isPicked || isCandidate ? 'normal' : 'italic',
              color: theme.palette.text.primary,
              textDecoration: rejected ? 'line-through' : 'none',
              // Wrap long HPO names to two lines instead of truncating; the tooltip
              // still carries the complete name for the rare >2-line term.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {node.name}
          </Typography>
          <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
            {added && (
              <CheckCircleIcon
                sx={{ fontSize: '0.85rem', color: theme.palette.success.main, mr: showRemove ? 0.25 : 0 }}
              />
            )}
            {showAccept && (
              <IconButton
                size="small"
                onClick={() => onAccept(node.id, node.name)}
                sx={{ p: '1px', color: theme.palette.success.main }}
              >
                <CheckIcon sx={{ fontSize: '0.8rem' }} />
              </IconButton>
            )}
            {showRestore && (
              <IconButton
                size="small"
                onClick={() => onRestore(node.id, node.name)}
                sx={{ p: '1px', color: theme.palette.success.main }}
              >
                <CheckIcon sx={{ fontSize: '0.8rem' }} />
              </IconButton>
            )}
            {showReject && (
              <IconButton
                size="small"
                onClick={() => onReject(node.id, node.name)}
                sx={{ p: '1px', color: theme.palette.error.main }}
              >
                <ClearIcon sx={{ fontSize: '0.8rem' }} />
              </IconButton>
            )}
            {showRemove && (
              <IconButton
                size="small"
                onClick={() => onRemove(node.id, node.name)}
                sx={{ p: '1px', color: theme.palette.error.main }}
              >
                <ClearIcon sx={{ fontSize: '0.8rem' }} />
              </IconButton>
            )}
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: isPicked || isCandidate ? 'fontFamilyMono' : 'inherit',
            fontSize: '0.55rem',
            lineHeight: 1.2,
            textTransform: isPicked || isCandidate ? 'none' : 'uppercase',
            letterSpacing: isPicked || isCandidate ? 0 : '0.4px',
            color: theme.palette.text.secondary,
          }}
        >
          {isPicked || isCandidate ? node.id : 'Category'}
        </Typography>
      </Box>
    </Tooltip>
  )
}

const HPOClusterGraph = ({
  graph,
  onAccept,
  onReject,
  onRemove,
  onRestore,
  selectedIds,
  rejectedIds,
  height = 340,
}) => {
  const theme = useTheme()
  const frameRef = useRef(null)
  const scrollRef = useRef(null)
  const contentRef = useRef(null)
  const legendRef = useRef(null)
  const { w: frameW, h: frameH } = useElementSize(frameRef)
  // The legend bar floats over the frame; reserve its measured height so the first
  // row of clusters (and their titles) never renders underneath it.
  const { h: legendH } = useElementSize(legendRef)
  const topInset = legendH ? legendH + 6 : 0 // legend height + its top:6 offset
  const [zoom, setZoom] = useState(null) // null = auto-fit; a number overrides the fit scale
  // Scroll correction to apply after a cursor-zoom re-render (keeps the point under
  // the cursor fixed), plus refs so the native wheel handler reads the latest scale
  // without being re-bound on every render.
  const pendingZoomRef = useRef(null)
  const zoomRef = useRef(zoom)
  const fitScaleRef = useRef(1)

  const layout = useMemo(() => {
    if (!graph || !graph.nodes || graph.nodes.length === 0) return null
    // Pack against the actual visible width so rows wrap exactly where the panel ends.
    const built = buildLayout(graph, frameW ? frameW - 2 * PAD : 0)
    return built.placedNodes.length > 0 ? built : null
  }, [graph, frameW])
  const hasLayout = !!layout

  const fitScale = useMemo(() => {
    if (!layout || !frameW || !frameH) return 1
    const availH = Math.max(frameH - topInset, 1)
    return Math.min(frameW / layout.width, availH / layout.height, 1)
  }, [layout, frameW, frameH, topInset])

  // Mirror the current scale into refs for the (once-bound) native wheel handler.
  zoomRef.current = zoom
  fitScaleRef.current = fitScale

  // Zoom with a trackpad pinch or Ctrl/⌘ + wheel, centered on the cursor. Plain wheel
  // is left alone so it still scrolls (pans) the panel. React 17 binds `wheel` as a
  // passive listener, so preventDefault only works through a native listener.
  useEffect(() => {
    if (!hasLayout) return undefined
    const el = scrollRef.current
    if (!el) return undefined
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return // plain scroll -> native pan
      e.preventDefault()
      const content = contentRef.current
      if (!content) return
      let dy = e.deltaY
      if (e.deltaMode === 1) dy *= 16
      // lines -> ~px
      else if (e.deltaMode === 2) dy *= 100 // pages -> ~px
      const oldScale = zoomRef.current != null ? zoomRef.current : fitScaleRef.current
      const newScale = Math.min(3, Math.max(0.2, oldScale * Math.pow(1.0015, -dy)))
      if (newScale === oldScale) return
      const rect = content.getBoundingClientRect()
      const cx = (e.clientX - rect.left) / oldScale
      const cy = (e.clientY - rect.top) / oldScale
      pendingZoomRef.current = { left: e.clientX - cx * newScale, top: e.clientY - cy * newScale }
      setZoom(newScale)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [hasLayout])

  // After a cursor-zoom re-render, nudge scroll so the cursor's target point holds
  // still. Runs every render but only acts when a zoom left work pending (cheap guard).
  useLayoutEffect(() => {
    const pending = pendingZoomRef.current
    const el = scrollRef.current
    const content = contentRef.current
    if (!pending || !el || !content) return
    const rect = content.getBoundingClientRect()
    el.scrollLeft += rect.left - pending.left
    el.scrollTop += rect.top - pending.top
    pendingZoomRef.current = null
  })

  // Re-fit whenever a new graph arrives.
  useEffect(() => {
    setZoom(null)
  }, [graph])

  if (!layout) return null

  const { placedNodes, headers, placedEdges, width, height: canvasH } = layout
  const edgeColor = theme.palette.divider
  const effectiveScale = zoom != null ? zoom : fitScale
  const zoomBy = (factor) => setZoom((z) => Math.min(3, Math.max(0.2, (z != null ? z : fitScale) * factor)))

  const legend = [
    { color: theme.palette.primary.main, label: 'Selected by AI' },
    { color: theme.palette.warning.main, label: 'Suggested by AI' },
    { color: theme.palette.text.secondary, label: 'Grouping term (shows how findings relate, not a finding itself)' },
    { color: theme.palette.success.main, label: 'Added to your analysis (✓ badge · ✗ to remove)', filled: true },
  ]

  return (
    <Box
      ref={frameRef}
      sx={{
        position: 'relative',
        height,
        width: '100%',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: theme.palette.action.hover,
      }}
    >
      {/* Single continuous top bar: the color key sits on the left and the zoom controls
          on the right, sharing one strip so the two never read as separate floating pills. */}
      <Box
        ref={legendRef}
        sx={{
          position: 'absolute',
          top: 6,
          left: 8,
          right: 8,
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: `${alpha(theme.palette.background.paper, 0.9)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap', minWidth: 0 }}>
          {legend.map((l) => (
            <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {l.filled ? (
                <CheckCircleIcon sx={{ fontSize: 14, color: l.color }} />
              ) : (
                <Box sx={{ width: 11, height: 11, borderRadius: '2px', backgroundColor: l.color }} />
              )}
              <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>{l.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => zoomBy(0.8)} sx={{ p: '2px' }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1, width: 14, textAlign: 'center' }}>
              −
            </Typography>
          </IconButton>
          <Tooltip title="Pinch, or hold Ctrl/⌘ and scroll, to zoom" arrow>
            <Typography
              sx={{
                fontSize: '0.75rem',
                minWidth: 38,
                textAlign: 'center',
                color: theme.palette.text.secondary,
                cursor: 'default',
              }}
            >
              {Math.round(effectiveScale * 100)}%
            </Typography>
          </Tooltip>
          <IconButton size="small" onClick={() => zoomBy(1.25)} sx={{ p: '2px' }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1, width: 14, textAlign: 'center' }}>
              +
            </Typography>
          </IconButton>
          <Button
            size="small"
            onClick={() => setZoom(null)}
            sx={{ minWidth: 0, px: 0.75, fontSize: '0.72rem', textTransform: 'none' }}
          >
            Fit
          </Button>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ width: '100%', height: '100%', overflow: 'auto', pt: `${topInset}px` }}>
        <Box ref={contentRef} sx={{ width: width * effectiveScale, height: canvasH * effectiveScale, mx: 'auto' }}>
          <Box
            sx={{
              position: 'relative',
              width,
              height: canvasH,
              transform: `scale(${effectiveScale})`,
              transformOrigin: 'top left',
            }}
          >
            <svg
              width={width}
              height={canvasH}
              style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
            >
              <defs>
                <marker
                  id="hpo-arrow"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={edgeColor} />
                </marker>
              </defs>
              {placedEdges.map((e) => {
                const midY = (e.y1 + e.y2) / 2
                return (
                  <path
                    key={e.id}
                    d={`M ${e.x1} ${e.y1} C ${e.x1} ${midY}, ${e.x2} ${midY}, ${e.x2} ${e.y2}`}
                    fill="none"
                    stroke={edgeColor}
                    strokeWidth={1.25}
                    markerEnd="url(#hpo-arrow)"
                  />
                )
              })}
            </svg>

            {headers.map((h) => (
              <Typography
                key={`header-${h.label}-${h.x}-${h.y}`}
                sx={{
                  position: 'absolute',
                  left: h.x,
                  top: h.y,
                  // Span the full cluster width so titles fit on one line where possible,
                  // then wrap to a second line instead of truncating with an ellipsis.
                  width: Math.max(h.width || NODE_W, NODE_W),
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontSize: '0.62rem',
                  lineHeight: 1.1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {h.label} ({h.count})
              </Typography>
            ))}

            {placedNodes.map((n) => (
              <ChipNode
                key={n.id}
                node={n}
                onAccept={onAccept}
                onReject={onReject}
                onRemove={onRemove}
                onRestore={onRestore}
                isSelected={selectedIds ? selectedIds.has(n.id) : false}
                isRejected={rejectedIds ? rejectedIds.has(n.id) : false}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default HPOClusterGraph
